import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import {
  Image,
  Calendar,
  Tag,
  X,
  ChevronLeft,
  ChevronRight,
  Grid,
  Filter
} from 'lucide-react';

const categories = [
  { value: 'all', label: 'All Photos' },
  { value: 'events', label: 'Community Events' },
  { value: 'volunteers', label: 'Volunteer Activities' },
  { value: 'infrastructure', label: 'Infrastructure Progress' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'community', label: 'Community Life' },
  { value: 'general', label: 'General' }
];

const GalleryPage = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/gallery?limit=100`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPhotos = filterCategory === 'all' 
    ? photos 
    : photos.filter(p => p.category === filterCategory);

  const openLightbox = (photo, index) => {
    setSelectedPhoto(photo);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedPhoto(null);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : filteredPhotos.length - 1;
    setCurrentIndex(newIndex);
    setSelectedPhoto(filteredPhotos[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex < filteredPhotos.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    setSelectedPhoto(filteredPhotos[newIndex]);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedPhoto) return;
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') closeLightbox();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhoto, currentIndex]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-plp-blue border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-plp-blue to-plp-blue-light text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <Image className="w-12 h-12 text-plp-gold" />
            <h1 className="font-heading text-4xl font-bold">Photo Gallery</h1>
          </div>
          <p className="text-xl text-blue-100 max-w-2xl">
            Browse photos from community events, volunteer activities, and the progress we're making together in Killarney.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filter Bar */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          <Filter className="w-5 h-5 text-slate-400 flex-shrink-0" />
          {categories.map(cat => (
            <Button
              key={cat.value}
              variant={filterCategory === cat.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterCategory(cat.value)}
              className={`flex-shrink-0 ${filterCategory === cat.value ? 'bg-plp-blue' : ''}`}
            >
              {cat.label}
            </Button>
          ))}
        </div>

        {filteredPhotos.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Image className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No photos in this category</h3>
              <p className="text-slate-500 mt-1">Check back later for new photos</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredPhotos.map((photo, index) => (
              <div 
                key={photo.id} 
                className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer shadow-md hover:shadow-xl transition-shadow"
                onClick={() => openLightbox(photo, index)}
              >
                <img 
                  src={photo.image_url} 
                  alt={photo.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-medium truncate">{photo.title}</h3>
                    {photo.event_name && (
                      <p className="text-white/80 text-sm flex items-center gap-1 mt-1">
                        <Tag className="w-3 h-3" /> {photo.event_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={!!selectedPhoto} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-5xl p-0 bg-black/95 border-none">
          <div className="relative">
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Navigation buttons */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
            
            {/* Image */}
            <div className="flex items-center justify-center min-h-[60vh] p-4">
              <img
                src={selectedPhoto?.image_url}
                alt={selectedPhoto?.title}
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
            
            {/* Info */}
            <div className="bg-black/80 text-white p-6">
              <h2 className="text-xl font-bold">{selectedPhoto?.title}</h2>
              {selectedPhoto?.description && (
                <p className="text-white/80 mt-2">{selectedPhoto?.description}</p>
              )}
              <div className="flex items-center gap-4 mt-4 text-sm text-white/60">
                {selectedPhoto?.event_name && (
                  <span className="flex items-center gap-1">
                    <Tag className="w-4 h-4" /> {selectedPhoto.event_name}
                  </span>
                )}
                {selectedPhoto?.date_taken && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> {selectedPhoto.date_taken}
                  </span>
                )}
                <span className="px-2 py-1 bg-white/10 rounded-full capitalize">
                  {selectedPhoto?.category}
                </span>
              </div>
              <p className="text-white/40 text-xs mt-4">
                {currentIndex + 1} / {filteredPhotos.length}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
